import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiShield, FiPlus, FiSearch, FiLock, FiAlertCircle, FiFolder, FiChevronDown, FiChevronRight, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiClock, FiActivity, FiUsers, FiBookOpen, FiLink, FiX, FiUpload, FiKey, FiServer, FiDatabase, FiCreditCard, FiWifi, FiFileText, FiSettings, FiAward, FiTerminal, FiDownload, FiFile, FiCopy } from "react-icons/fi";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import CredentialModal from "../../components/vault/CredentialModal";
import MasterPasswordModal from "../../components/vault/MasterPasswordModal";
import MasterPasswordResetModal from "../../components/vault/MasterPasswordResetModal";
import VaultLockScreen from "../../components/vault/VaultLockScreen";
import VaultPermissionManager from "../../components/vault/VaultPermissionManager";
import AccessPasswordModal from "../../components/vault/AccessPasswordModal";
import BulkImportModal from "../../components/vault/BulkImportModal";
import MultiCredentialModal from "../../components/vault/MultiCredentialModal";
import DeleteVaultModal from "../../components/vault/DeleteVaultModal";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import {
  CredentialEntry as DecryptedEntry,
  decryptCredentialUnified,
  decryptPrivateKey,
  validatePasswordStrength,
  generateRSAKeyPair,
  encryptPrivateKey,
  encryptCredential,
  encryptWithPublicKey,
  decryptNotes,
  decryptNotesUnified,
  encryptNotes,
  base64ToArrayBuffer
} from "../../utils/crypto/vault-crypto";
import {
  getDefaultEntries,
  getLabelPlaceholder,
  entryTypes
} from "../../components/vault/credential-utils";
import {
  getVaultsByCompany,
  createVault,
  updateVault,
  deleteVault,
  createCredentialEntry,
  updateCredentialEntry,
  deleteCredentialEntry,
  logCredentialAccess,
  linkVaultToProgram,
  unlinkVaultFromProgram,
  getLinkedProgramsForVault,
  LinkedProgramInfo,
  Vault,
  CredentialEntry,
  CredentialType,
  CreateCredentialEntryData,
  UpdateCredentialEntryData,
  VaultUserPermissions,
  LinkVaultOptions,
  VerificationKeyData
} from "../../utils/api/endpoints/vault";
import { useGetProgramsQuery } from "../../utils/api/endpoints/programsApi";
import { CTBProgram } from "../../utils/api/endpoints/programs/parsePrograms";
import { listAllCompanyTargets } from "../../utils/api/endpoints/companies";
import toast from "react-hot-toast";

const credentialTypes = [
  { id: 'password', label: 'Login / Password', description: 'Username and password for websites or apps', icon: FiKey },
  { id: 'api_key', label: 'API Key', description: 'API keys, tokens, and secrets', icon: FiLock },
  { id: 'ssh_key', label: 'SSH Key', description: 'SSH private keys and passphrases', icon: FiTerminal },
  { id: 'certificate', label: 'Certificate', description: 'SSL/TLS certificates and keys', icon: FiFileText },
  { id: 'database', label: 'Database', description: 'Database connection credentials', icon: FiDatabase },
  { id: 'server', label: 'Server', description: 'Server access credentials', icon: FiServer },
  { id: 'wifi', label: 'Wi-Fi', description: 'Wi-Fi network passwords', icon: FiWifi },
  { id: 'credit_card', label: 'Credit Card', description: 'Payment card information', icon: FiCreditCard },
  { id: 'secure_note', label: 'Secure Note', description: 'Encrypted text notes', icon: FiFileText },
  { id: 'custom', label: 'Custom', description: 'Custom credential type', icon: FiSettings },
];

// Session timeout configuration
const SESSION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const WARNING_THRESHOLD_SECONDS = 60; // Show warning at 60 seconds

// Helper function to get icon component for credential type
const CredentialTypeIcon: React.FC<{ type?: string; className?: string }> = ({ type, className = "w-5 h-5" }) => {
  switch (type) {
    case "password": return <FiKey className={className} />;
    case "api_key": return <FiLock className={className} />;
    case "ssh_key": return <FiServer className={className} />;
    case "certificate": return <FiAward className={className} />;
    case "database": return <FiDatabase className={className} />;
    case "server": return <FiServer className={className} />;
    case "secure_note": return <FiFileText className={className} />;
    case "wifi": return <FiWifi className={className} />;
    case "credit_card": return <FiCreditCard className={className} />;
    case "custom": return <FiSettings className={className} />;
    default: return <FiKey className={className} />;
  }
};

const CredentialVaultPage: React.FC = () => {
  usePageTitle("Credential Vault | Capture The Bug");
  const { role } = useUserCredentials();

  const [vaults, setVaults] = useState<Vault[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [masterPassword, setMasterPassword] = useState<string | null>(null);
  const [showMasterPasswordModal, setShowMasterPasswordModal] = useState(false);
  const [showMasterPasswordResetModal, setShowMasterPasswordResetModal] = useState(false);
  const [showVaultModal, setShowVaultModal] = useState(false);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [editingVault, setEditingVault] = useState<Vault | null>(null);
  const [editingEntry, setEditingEntry] = useState<{ vault: Vault; entry?: CredentialEntry } | null>(null);
  const [selectedVaultId, setSelectedVaultId] = useState<number | null>(null);
  const selectedVault = vaults.find(v => v.vault_id === selectedVaultId);

  // Persistent master password & deletion modals state
  const [hasMasterPasswordConfigured, setHasMasterPasswordConfigured] = useState<boolean>(false);
  const [historicalVerificationKey, setHistoricalVerificationKey] = useState<VerificationKeyData | null>(null);
  const [forceCreateMasterPassword, setForceCreateMasterPassword] = useState<boolean>(false);
  const [vaultToDelete, setVaultToDelete] = useState<Vault | null>(null);
  const [isDeleteVaultModalOpen, setIsDeleteVaultModalOpen] = useState(false);
  const [deleteModalLinkedPrograms, setDeleteModalLinkedPrograms] = useState<LinkedProgramInfo[]>([]);
  const [isLoadingLinkedProgramsForDelete, setIsLoadingLinkedProgramsForDelete] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<{ vaultId: number; entryId: number; label: string } | null>(null);
  const [programToUnlink, setProgramToUnlink] = useState<{ programId: number; title: string } | null>(null);
  const isSubUser = role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER;
  const canManage = role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR;
  const [decryptedEntries, setDecryptedEntries] = useState<Map<number, { entries: DecryptedEntry[], notes?: string }>>(new Map());
  const [pendingAction, setPendingAction] = useState<{
    type: "view" | "edit" | "create" | "createVaultData" | "addEntry" | "bulkAdd";
    vault?: Vault;
    entry?: CredentialEntry;
    vaultData?: { name: string; description: string };
  } | null>(null);

  // Session timeout state
  const [isSessionLocked, setIsSessionLocked] = useState(false);
  const [wasTimedOut, setWasTimedOut] = useState(false);
  const [secondsUntilLock, setSecondsUntilLock] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Audit trail state

  // Permission manager state
  const [showPermissionManager, setShowPermissionManager] = useState(false);
  const [showCredentialHint, setShowCredentialHint] = useState(false);
  const [isManagementViewActive, setIsManagementViewActive] = useState(false);
  const [isConventionExpanded, setIsConventionExpanded] = useState(false);
  const [managementTab, setManagementTab] = useState<'single' | 'bulk' | 'import' | 'links'>('single');
  const [showValues, setShowValues] = useState<Record<number, boolean>>({});
  const [managementStep, setManagementStep] = useState<1 | 2 | 3>(1);
  const [selectedType, setSelectedType] = useState<string>('password'); // Default to password
  const [targetUrl, setTargetUrl] = useState("");
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);
  const [allTargets, setAllTargets] = useState<{targetName: string, targetType: string}[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  useEffect(() => {
    // Select first vault by default if none selected
    if (vaults.length > 0 && selectedVaultId === null) {
      setSelectedVaultId(vaults[0].vault_id);
    }

    // Show hint if there is at least one vault and it has no entries
    if (vaults.length > 0 && vaults.some(v => (v.entries?.length || 0) === 0)) {
      setShowCredentialHint(true);
    } else {
      setShowCredentialHint(false);
    }
  }, [vaults, selectedVaultId]);
  const [permissionManagerVault, setPermissionManagerVault] = useState<Vault | null>(null);

  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showMultiCredentialModal, setShowMultiCredentialModal] = useState(false);
  const [bulkImportVault, setBulkImportVault] = useState<Vault | null>(null);
  const [credentialLabel, setCredentialLabel] = useState("");
  const [credentialEntries, setCredentialEntries] = useState<DecryptedEntry[]>([]);
  const [credentialNotes, setCredentialNotes] = useState("");
  const [credentialIsFavorite, setCredentialIsFavorite] = useState(false);
  const [credentialExpiresAt, setCredentialExpiresAt] = useState("");
  const [credentialRotationDays, setCredentialRotationDays] = useState(30);
  const [isSavingEntry, setIsSavingEntry] = useState(false);
  const [isSavingVault, setIsSavingVault] = useState(false);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [activeBulkTargetDropdownId, setActiveBulkTargetDropdownId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Bulk entries for inline management
  const [bulkEntryRows, setBulkEntryRows] = useState<any[]>([
    { id: 1, type: 'password', label: '', url: '', entries: getDefaultEntries('password'), is_favorite: false }
  ]);

  // Sub-user permissions state (only populated for Developer/BusinessManager roles)
  const [vaultPermissions, setVaultPermissions] = useState<Record<number, VaultUserPermissions>>({});

  // Link to Program state
  const [showLinkProgramModal, setShowLinkProgramModal] = useState(false);
  const [selectedLinkVault, setSelectedLinkVault] = useState<Vault | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<CTBProgram | null>(null);
  const [showAccessPasswordModal, setShowAccessPasswordModal] = useState(false);
  const [linkedPrograms, setLinkedPrograms] = useState<LinkedProgramInfo[]>([]);
  const [loadingLinkedPrograms, setLoadingLinkedPrograms] = useState(false);
  const [unlinkingProgramId, setUnlinkingProgramId] = useState<number | null>(null);

  // Helper function to reset all management states for fresh Add Entry flow
  const resetManagementStates = useCallback(() => {
    setCredentialLabel("");
    setTargetUrl("");
    setSelectedType('password');
    setCredentialEntries(getDefaultEntries('password'));
    setCredentialNotes("");
    setCredentialIsFavorite(false);
    setCredentialExpiresAt("");
    setCredentialRotationDays(30);
    setManagementStep(1);
    setShowValues({});
    setEditingEntry(null);
  }, []);


  // Fetch programs for linking (Business Owner only)
  const { data: programsData, isLoading: loadingPrograms } = useGetProgramsQuery(
    { limit: 100 },
    { skip: !showLinkProgramModal }
  );
  const programs = programsData?.programs || [];

  // Fetch all company targets from assets
  useEffect(() => {
    const fetchTargetsFromAssets = async () => {
      try {
        setLoadingTargets(true);
        // Using the new standalone endpoint for all company targets
        const targets = await listAllCompanyTargets();
        const formattedTargets = targets.map((t: any) => ({
          targetName: t.target_value,
          targetType: t.target_type
        }));
        setAllTargets(formattedTargets);
      } catch (error) {
        console.error("Error fetching all company targets:", error);
      } finally {
        setLoadingTargets(false);
      }
    };

    fetchTargetsFromAssets();
  }, []);


  // Helper function to check if sub-user can add to a specific vault
  const canAddToVault = (vaultId: number): boolean => {
    if (canManage) return true; // Only Business Owner can always add
    return vaultPermissions[vaultId]?.can_add ?? false;
  };

  // Check if master password has been set before
  // Persists even if all active vaults are deleted, by consulting tenant configuration & historical keys
  const hasMasterPasswordBeenSet =
    hasMasterPasswordConfigured ||
    vaults.some(vault =>
      vault.encrypted_private_key ||
      vault.entries?.some(entry => entry.salt)
    );

  // Clear all session timers
  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Lock the vault session
  const lockSession = useCallback(() => {
    clearTimers();
    setMasterPassword(null);
    setIsSessionLocked(true);
    setSecondsUntilLock(null);
    setDecryptedEntries(new Map()); // Clear decrypted data
    setWasTimedOut(true);
    toast.error("Vault locked due to inactivity", { icon: "🔒" });
  }, [clearTimers]);

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    if (!masterPassword) return;

    const now = Date.now();
    lastActivityRef.current = now;

    clearTimers();

    // Set lock timeout
    timerRef.current = setTimeout(() => {
      lockSession();
    }, SESSION_TIMEOUT_MS);

    // Set countdown interval
    countdownRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, Math.ceil((SESSION_TIMEOUT_MS - elapsed) / 1000));
      setSecondsUntilLock(remaining);

      // Show warning toast
      if (remaining === WARNING_THRESHOLD_SECONDS) {
        toast("Vault will lock in 1 minute due to inactivity", {
          icon: "⏰",
          duration: 5000
        });
      }

      if (remaining <= 0) {
        clearTimers();
      }
    }, 1000);
  }, [masterPassword, clearTimers, lockSession]);

  // Activity event listeners for session timeout
  useEffect(() => {
    if (!masterPassword) return;

    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
    const handleActivity = () => resetActivityTimer();

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    resetActivityTimer();

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [masterPassword, resetActivityTimer, clearTimers]);

  const fetchVaults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getVaultsByCompany();
      setVaults(response.vaults);
      if (response.hasMasterPasswordConfigured !== undefined) {
        setHasMasterPasswordConfigured(response.hasMasterPasswordConfigured);
      }
      if (response.verificationKey) {
        setHistoricalVerificationKey(response.verificationKey);
      }
      // For sub-users, also set their permissions
      if (response.permissions) {
        setVaultPermissions(response.permissions);
      }
    } catch (error) {
      console.error("Error fetching vaults:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedVault) {
      setLoadingLinkedPrograms(true);
      getLinkedProgramsForVault(selectedVault.vault_id)
        .then(({ linkedPrograms: linked }) => setLinkedPrograms(linked))
        .catch(err => console.error(err))
        .finally(() => setLoadingLinkedPrograms(false));
    }
  }, [selectedVault]);

  useEffect(() => {
    fetchVaults();
  }, [fetchVaults]);

  const handleMasterPasswordSubmit = async (password: string) => {
    const validation = validatePasswordStrength(password);
    if (!validation.isValid) {
      toast.error(validation.feedback[0] || "Password does not meet requirements");
      throw new Error(validation.feedback[0] || "Password does not meet requirements");
    }

    // Cryptographic check when unlocking existing master password
    const isUnlockingExisting = !forceCreateMasterPassword && hasMasterPasswordBeenSet;
    if (isUnlockingExisting) {
      const keyToVerify = vaults.find(v => v.encrypted_private_key)?.encrypted_private_key
        ? vaults.find(v => v.encrypted_private_key)!
        : historicalVerificationKey;

      if (
        keyToVerify?.encrypted_private_key &&
        keyToVerify.private_key_iv &&
        keyToVerify.private_key_salt &&
        keyToVerify.private_key_auth_tag
      ) {
        try {
          await decryptPrivateKey({
            encrypted_private_key: keyToVerify.encrypted_private_key,
            private_key_iv: keyToVerify.private_key_iv,
            private_key_salt: keyToVerify.private_key_salt,
            private_key_auth_tag: keyToVerify.private_key_auth_tag
          }, password);
        } catch {
          toast.error("Incorrect Master Password. Please check and try again.");
          throw new Error("Incorrect Master Password");
        }
      }
    }

    setMasterPassword(password);
    setShowMasterPasswordModal(false);
    setForceCreateMasterPassword(false);
    setIsSessionLocked(false);
    setWasTimedOut(false);

    if (pendingAction) {
      if (pendingAction.type === "create") {
        setEditingVault(null);
        setShowVaultModal(true);
      } else if (pendingAction.type === "createVaultData" && pendingAction.vaultData) {
        handleSaveVault(pendingAction.vaultData, password);
      } else if (pendingAction.type === "addEntry" && pendingAction.vault) {
        resetManagementStates();
        setEditingEntry({ vault: pendingAction.vault });
        setIsManagementViewActive(true);
        setManagementTab('single');
      } else if (pendingAction.type === "bulkAdd" && pendingAction.vault) {
        setBulkImportVault(pendingAction.vault);
        setBulkEntryRows([{ id: Date.now(), type: 'password', label: '', url: '', entries: getDefaultEntries('password'), is_favorite: false }]);
        setIsManagementViewActive(true);
        setManagementTab('bulk');
      } else if (pendingAction.type === "edit" && pendingAction.vault && pendingAction.entry) {
        const entry = pendingAction.entry;
        const vault = pendingAction.vault;
        
        // Decrypt the entry first
        let decrypted = decryptedEntries.get(entry.entry_id);
        if (!decrypted) {
          try {
            const result = await decryptCredentialUnified(
              {
                entry_id: entry.entry_id,
                encrypted_data: entry.encrypted_data,
                iv: entry.iv,
                auth_tag: entry.auth_tag,
                salt: entry.salt,
                key_iterations: entry.key_iterations,
                encrypted_credential_key: entry.encrypted_credential_key
              },
              password,
              vault ? {
                encrypted_private_key: vault.encrypted_private_key,
                private_key_iv: vault.private_key_iv,
                private_key_salt: vault.private_key_salt,
                private_key_auth_tag: vault.private_key_auth_tag
              } : undefined
            );
            const decryptedData = { entries: result.entries, notes: "" };
            
            // Decrypt notes if they exist
            let decryptedNotes = "";
            if (entry.encrypted_notes && entry.notes_iv && entry.notes_auth_tag) {
              try {
                decryptedNotes = await decryptNotesUnified(
                  {
                    encrypted_notes: entry.encrypted_notes,
                    notes_iv: entry.notes_iv,
                    notes_auth_tag: entry.notes_auth_tag,
                    salt: entry.salt,
                    key_iterations: entry.key_iterations,
                    encrypted_credential_key: entry.encrypted_credential_key
                  },
                  password,
                  vault ? {
                    encrypted_private_key: vault.encrypted_private_key,
                    private_key_iv: vault.private_key_iv,
                    private_key_salt: vault.private_key_salt,
                    private_key_auth_tag: vault.private_key_auth_tag
                  } : undefined
                );
              } catch (err) {
                console.error("Failed to decrypt notes during pending edit:", err);
              }
            }
            
            decrypted = { entries: decryptedData.entries, notes: decryptedNotes };
            setDecryptedEntries(prev => new Map(prev).set(entry.entry_id, decrypted!));
          } catch (err) {
            console.error("Decryption failed during pending edit:", err);
            toast.error("Failed to decrypt entry");
            setPendingAction(null);
            return;
          }
        }

        // Populate management states
        setCredentialLabel(entry.label);
        setSelectedType(entry.credential_type || 'password');
        setTargetUrl(entry.url || "");
        setCredentialEntries(
          decrypted?.entries && decrypted.entries.length > 0
            ? decrypted.entries
            : getDefaultEntries(entry.credential_type || 'password')
        );
        setCredentialNotes(decrypted?.notes || "");
        setCredentialIsFavorite(entry.is_favorite || false);
        setCredentialExpiresAt(entry.expires_at ? entry.expires_at.split('T')[0] : "");
        setCredentialRotationDays(entry.rotation_reminder_days || 30);
        
        setEditingEntry({ vault, entry });
        setIsManagementViewActive(true);
        setManagementTab('single');
        setManagementStep(1);
        setShowValues({});
      } else if (pendingAction.type === "view" && pendingAction.entry) {
        // Pass the vault from pending action (contains private key data for public key decryption)
        await handleDecryptEntry(pendingAction.entry, password, pendingAction.vault);
      }
      setPendingAction(null);
    }
  };

  // Handle unlock from lock screen
  const handleUnlockFromLockScreen = (password: string) => {
    const validation = validatePasswordStrength(password);
    if (!validation.isValid) {
      toast.error(validation.feedback[0] || "Password does not meet requirements");
      return;
    }
    setMasterPassword(password);
    setIsSessionLocked(false);
    setWasTimedOut(false);
    toast.success("Vault unlocked", { icon: "🔓" });
  };

  const handleDecryptEntry = async (entry: CredentialEntry, password: string, providedVault?: Vault) => {
    try {
      // Use provided vault or find it from state (needed for public key decryption)
      const vault = providedVault || vaults.find(v => v.vault_id === entry.vault_id);

      // Use unified decryption that handles both master password and public key modes
      const decrypted = await decryptCredentialUnified(
        {
          entry_id: entry.entry_id,
          encrypted_data: entry.encrypted_data,
          iv: entry.iv,
          auth_tag: entry.auth_tag,
          salt: entry.salt,
          key_iterations: entry.key_iterations,
          encrypted_credential_key: entry.encrypted_credential_key
        },
        password,
        vault ? {
          encrypted_private_key: vault.encrypted_private_key,
          private_key_iv: vault.private_key_iv,
          private_key_salt: vault.private_key_salt,
          private_key_auth_tag: vault.private_key_auth_tag
        } : undefined
      );

      // Also decrypt notes if they exist
      let decryptedNotes = "";
      if (entry.encrypted_notes && entry.notes_iv && entry.notes_auth_tag) {
        try {
          decryptedNotes = await decryptNotesUnified(
            {
              encrypted_notes: entry.encrypted_notes,
              notes_iv: entry.notes_iv,
              notes_auth_tag: entry.notes_auth_tag,
              salt: entry.salt,
              key_iterations: entry.key_iterations,
              encrypted_credential_key: entry.encrypted_credential_key
            },
            password,
            vault ? {
              encrypted_private_key: vault.encrypted_private_key,
              private_key_iv: vault.private_key_iv,
              private_key_salt: vault.private_key_salt,
              private_key_auth_tag: vault.private_key_auth_tag
            } : undefined
          );
        } catch (err) {
          console.error("Failed to decrypt notes:", err);
        }
      }

      const decryptedResult = { entries: decrypted.entries, notes: decryptedNotes };
      setDecryptedEntries(prev => new Map(prev).set(entry.entry_id, decryptedResult));
      await logCredentialAccess(entry.vault_id, "decrypt");
      return decryptedResult;
    } catch (error) {
      console.error("Decryption error:", error);
      toast.error("Failed to decrypt. Check your master password.");
      setMasterPassword(null);
      return null;
    }
  };

  const handleVaultSelect = (vaultId: number) => {
    setSelectedVaultId(vaultId);
  };

  const handleAddVault = () => {
    setEditingVault(null);
    if (!masterPassword) {
      setPendingAction({ type: "create" });
      setShowMasterPasswordModal(true);
    } else {
      setShowVaultModal(true);
    }
  };

  const handleEditVault = (vault: Vault) => {
    setEditingVault(vault);
    setShowVaultModal(true);
  };

  const handleAddEntry = (vault: Vault) => {
    // Sub-users don't need master password - they use public key encryption
    if (isSubUser) {
      if (!vault.public_key) {
        toast.error("This vault doesn't support sub-user additions (no public key)");
        return;
      }
      resetManagementStates();
      setEditingEntry({ vault });
      setIsManagementViewActive(true);
      setManagementTab('single');
      setShowValues({});
    } else {
      // Owner/admin mode: Require master password for symmetric encryption
      if (!masterPassword) {
        setPendingAction({ type: "addEntry", vault });
        setShowMasterPasswordModal(true);
      } else {
        resetManagementStates();
        setEditingEntry({ vault });
        setIsManagementViewActive(true);
        setManagementTab('single');
        setShowValues({});
      }
    }
  };

  const handleBulkAdd = (vault: Vault) => {
    if (isSubUser) {
      if (!vaultPermissions[vault.vault_id]?.can_add && !canManage) {
        toast.error("You don't have permission to add credentials to this vault");
        return;
      }
      setBulkImportVault(vault);
      setBulkEntryRows([{ id: 1, type: 'password', label: '', url: '', username: '', password: '', is_favorite: false }]);
      setIsManagementViewActive(true);
      setManagementTab('bulk');
    } else {
      // Owner/admin mode: Require master password for symmetric encryption
      if (!masterPassword) {
        setPendingAction({ type: "bulkAdd", vault });
        setShowMasterPasswordModal(true);
      } else {
        setBulkImportVault(vault);
        setBulkEntryRows([{ id: Date.now(), type: 'password', label: '', url: '', entries: getDefaultEntries('password'), is_favorite: false }]);
        setIsManagementViewActive(true);
        setManagementTab('bulk');
      }
    }
  };

  const handleDeleteVaultClick = async (vault: Vault) => {
    setVaultToDelete(vault);
    setIsDeleteVaultModalOpen(true);
    setIsLoadingLinkedProgramsForDelete(true);
    try {
      const { linkedPrograms } = await getLinkedProgramsForVault(vault.vault_id);
      setDeleteModalLinkedPrograms(linkedPrograms || []);
    } catch (err) {
      console.error("Error fetching linked programs for vault deletion:", err);
      setDeleteModalLinkedPrograms([]);
    } finally {
      setIsLoadingLinkedProgramsForDelete(false);
    }
  };

  const handleConfirmDeleteVault = async (vaultId: number) => {
    await deleteVault(vaultId);
    setVaults(prev => prev.filter(v => v.vault_id !== vaultId));
    if (selectedVaultId === vaultId) {
      const remaining = vaults.filter(v => v.vault_id !== vaultId);
      setSelectedVaultId(remaining.length > 0 ? remaining[0].vault_id : null);
    }
  };

  const handleEditEntry = async (vault: Vault, entry: CredentialEntry) => {
    if (!masterPassword) {
      setPendingAction({ type: "edit", vault, entry });
      setShowMasterPasswordModal(true);
    } else {
      // Decrypt the entry first before opening the management view
      let decrypted = decryptedEntries.get(entry.entry_id);
      if (!decrypted) {
        const result = await handleDecryptEntry(entry, masterPassword, vault);
        if (!result) {
          // Decryption failed or master password was invalid
          return;
        }
        decrypted = result;
      }
      
      try {
        // Populate management states
        setCredentialLabel(entry.label);
        setSelectedType(entry.credential_type || 'password');
        setTargetUrl(entry.url || "");
        setCredentialEntries(
          decrypted?.entries && decrypted.entries.length > 0
            ? decrypted.entries
            : getDefaultEntries(entry.credential_type || 'password')
        );
        setCredentialNotes(decrypted?.notes || "");
        setCredentialIsFavorite(entry.is_favorite || false);
        setCredentialExpiresAt(entry.expires_at ? entry.expires_at.split('T')[0] : "");
        setCredentialRotationDays(entry.rotation_reminder_days || 30);
        
        setEditingEntry({ vault, entry });
        setIsManagementViewActive(true);
        setManagementTab('single');
        setManagementStep(1);
        setShowValues({});
      } catch (err) {
        console.error("Decryption failed during edit:", err);
        toast.error("Failed to decrypt entry for editing");
      }
    }
  };

  const handleViewEntry = (entry: CredentialEntry) => {
    if (!canManage) return;
    if (decryptedEntries.has(entry.entry_id)) return;
    // Find the vault for this entry (needed for public key decryption)
    const vault = vaults.find(v => v.vault_id === entry.vault_id);
    if (!masterPassword) {
      setPendingAction({ type: "view", entry, vault });
      setShowMasterPasswordModal(true);
    } else {
      handleDecryptEntry(entry, masterPassword, vault);
    }
  };

  const handleHideEntry = (entryId: number) => {
    setDecryptedEntries(prev => {
      const newMap = new Map(prev);
      newMap.delete(entryId);
      return newMap;
    });
  };

  const handleCopyPassword = async (entry: CredentialEntry) => {
    if (!canManage) return;
    try {
      let decrypted = decryptedEntries.get(entry.entry_id);
      
      if (!decrypted) {
        const vault = vaults.find(v => v.vault_id === entry.vault_id);
        if (!masterPassword) {
           setPendingAction({ type: "view", entry, vault });
           setShowMasterPasswordModal(true);
           return;
        }
        
        const result = await decryptCredentialUnified(
          {
            entry_id: entry.entry_id,
            encrypted_data: entry.encrypted_data,
            iv: entry.iv,
            auth_tag: entry.auth_tag,
            salt: entry.salt,
            key_iterations: entry.key_iterations,
            encrypted_credential_key: entry.encrypted_credential_key
          },
          masterPassword,
          vault ? {
            encrypted_private_key: vault.encrypted_private_key,
            private_key_iv: vault.private_key_iv,
            private_key_salt: vault.private_key_salt,
            private_key_auth_tag: vault.private_key_auth_tag
          } : undefined
        );
        const decryptedData = { entries: result.entries, notes: "" };
        
        // Decrypt notes just in case (though not needed for copy-password, keeps state consistent)
        let decryptedNotes = "";
        try {
          decryptedNotes = await decryptNotesUnified(
            {
              encrypted_notes: entry.encrypted_notes,
              notes_iv: entry.notes_iv,
              notes_auth_tag: entry.notes_auth_tag,
              salt: entry.salt,
              key_iterations: entry.key_iterations,
              encrypted_credential_key: entry.encrypted_credential_key
            },
            masterPassword!,
            vault ? {
              encrypted_private_key: vault.encrypted_private_key,
              private_key_iv: vault.private_key_iv,
              private_key_salt: vault.private_key_salt,
              private_key_auth_tag: vault.private_key_auth_tag
            } : undefined
          );
        } catch (err) {
          console.error("Failed to decrypt notes during copy:", err);
        }

        decrypted = { entries: decryptedData.entries, notes: decryptedNotes };
        setDecryptedEntries(prev => new Map(prev).set(entry.entry_id, decrypted!));
      }

      const secretField = decrypted?.entries?.find(f => f.type === 'password' || f.key.toLowerCase().includes('key'));
      const valueToCopy = secretField?.value || decrypted?.entries?.[0]?.value;

      if (valueToCopy) {
        await navigator.clipboard.writeText(valueToCopy);
        toast.success("Password copied to clipboard");
        await logCredentialAccess(entry.vault_id, "view");
      }
    } catch (error) {
      console.error("Copy error:", error);
      toast.error("Failed to copy password");
    }
  };

  const handleDeleteEntryClick = (vaultId: number, entryId: number, label: string) => {
    setEntryToDelete({ vaultId, entryId, label });
  };

  const handleConfirmDeleteEntry = async () => {
    if (!entryToDelete) return;
    const { vaultId, entryId } = entryToDelete;
    try {
      await deleteCredentialEntry(vaultId, entryId);
      setVaults(prev => prev.map(v => {
        if (v.vault_id === vaultId) {
          return { ...v, entries: v.entries?.filter(e => e.entry_id !== entryId) };
        }
        return v;
      }));
      setDecryptedEntries(prev => {
        const newMap = new Map(prev);
        newMap.delete(entryId);
        return newMap;
      });
      toast.success("Credential deleted successfully");
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete credential");
    } finally {
      setEntryToDelete(null);
    }
  };

  // Handle opening link to program modal (only for Business Owner)
  const handleLinkToProgram = async (vault: Vault) => {
    if (!canManage) return; // Extra safety check
    setSelectedLinkVault(vault);
    setShowLinkProgramModal(true);
    setLoadingLinkedPrograms(true);

    try {
      const { linkedPrograms: linked } = await getLinkedProgramsForVault(vault.vault_id);
      setLinkedPrograms(linked);
    } catch (error) {
      console.error("Error fetching linked programs:", error);
    } finally {
      setLoadingLinkedPrograms(false);
    }
  };

  // Handle program selection for linking
  const handleSelectProgram = (program: CTBProgram) => {
    setSelectedProgram(program);
    setShowLinkProgramModal(false);
    setShowAccessPasswordModal(true);
  };

  // Handle unlinking a program
  const handleUnlinkProgram = async (programId: number) => {
    // Robustly determine vaultId from either the specific linking state or the active vault selection
    const vaultId = selectedLinkVault?.vault_id || selectedVault?.vault_id;
    if (!vaultId) return;

    setUnlinkingProgramId(programId);
    try {
      await unlinkVaultFromProgram(vaultId, programId);
      setLinkedPrograms(prev => prev.filter(p => p.programId !== programId));
    } catch (error) {
      console.error("Error unlinking program:", error);
    } finally {
      setUnlinkingProgramId(null);
    }
  };

  const handleDownloadTemplate = (format: 'csv' | 'json') => {
    const headers = [
      'label', 'credential_type', 'username', 'password', 'url', 'notes', 'is_favorite', 'rotation_reminder_days', 'expires_at'
    ];
    
    const sampleData = [
      {
        label: 'Sample Google Account',
        credential_type: 'password',
        username: 'user@gmail.com',
        password: 'password123',
        url: 'https://google.com',
        notes: 'Personal account',
        is_favorite: 'true',
        rotation_reminder_days: '30',
        expires_at: '2026-12-31'
      }
    ];

    let content = '';
    let mimeType = '';
    let fileName = '';

    if (format === 'csv') {
      content = [
        headers.join(','),
        ...sampleData.map(row => headers.map(h => `"${(row as any)[h] || ''}"`).join(','))
      ].join('\n');
      mimeType = 'text/csv';
      fileName = 'vault_template.csv';
    } else {
      content = JSON.stringify(sampleData, null, 2);
      mimeType = 'application/json';
      fileName = 'vault_template.json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`${format.toUpperCase()} template downloaded`);
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        let data: any[] = [];

        if (file.name.endsWith('.json')) {
          data = JSON.parse(content);
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n');
          if (lines.length < 2) throw new Error("File is empty");
          const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
          data = lines.slice(1).filter((l: string) => l.trim()).map((line: string) => {
             const values = line.split(',').map((v: string) => v.replace(/"/g, '').trim());
             const obj: any = {};
             headers.forEach((h: string, i: number) => {
               obj[h] = values[i];
             });
             return obj;
          });
        } else {
          toast.error("Unsupported file format");
          return;
        }

        if (!Array.isArray(data)) {
          toast.error("Invalid file format: expected an array of credentials");
          return;
        }

        // Map to bulk rows for review
        const newRows = data.map((item: any, idx: number) => ({
          id: Date.now() + idx,
          type: item.credential_type || 'password',
          label: item.label || 'Untitled Credential',
          url: item.url || '',
          is_favorite: item.is_favorite === 'true' || item.is_favorite === true,
          entries: [
            ...getDefaultEntries(item.credential_type || 'password').map((e: any) => ({
               ...e,
               value: item[e.key.toLowerCase()] || item[e.key] || (e.key === 'username' ? item.username : '') || (e.key === 'password' ? item.password : '') || ''
            }))
          ],
          notes: item.notes || '',
          expires_at: item.expires_at ? item.expires_at.split('T')[0] : '',
          rotation_reminder_days: item.rotation_reminder_days || '30'
        }));

        setBulkEntryRows(newRows);
        setManagementTab('bulk');
        toast.success(`Loaded ${newRows.length} credentials for review. Click Save All to finish.`);
      } catch (err) {
        console.error(err);
        toast.error("Failed to parse file. Please ensure it follows the template format.");
      }
    };
    reader.readAsText(file);
  };

  // Handle access password submission for linking
  const handleAccessPasswordSubmit = async (options: LinkVaultOptions) => {
    if (!selectedLinkVault || !selectedProgram?.id) return;
    try {
      await linkVaultToProgram(selectedLinkVault.vault_id, selectedProgram.id, options);
      setShowAccessPasswordModal(false);
      // Update linked programs list
      setLinkedPrograms(prev => [...prev, {
        programId: selectedProgram.id,
        programTitle: selectedProgram.title,
        profilePicture: selectedProgram.profilePicture,
        isActivated: selectedProgram.isActivated,
        linkedAt: new Date().toISOString(),
        accessType: options.accessType || "unlimited"
      }]);
      setSelectedLinkVault(null);
      setSelectedProgram(null);
    } catch (error) {
      console.error("Error linking vault:", error);
      throw error;
    }
  };

  const filteredVaults = vaults.filter(
    v => v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
         v.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         v.entries?.some(e => e.label.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleSaveVault = async (data: { name: string; description: string }, overridePassword?: string) => {
    if (isSavingVault) return;
    const activePassword = overridePassword || masterPassword;
    try {
      setIsSavingVault(true);
      if (editingVault) {
        await updateVault(editingVault.vault_id, data);
      } else {
        // For new vaults, generate RSA key pair for asymmetric encryption
        // This enables sub-users to add credentials without being able to decrypt them
        if (!activePassword) {
          toast.error("Master password required to create vault with encryption keys");
          setPendingAction({ type: "createVaultData", vaultData: data });
          setShowMasterPasswordModal(true);
          return;
        }

        // Generate RSA key pair
        const keyPair = await generateRSAKeyPair();

        // Encrypt the private key with the master password
        const encryptedPrivateKey = await encryptPrivateKey(keyPair.privateKey, activePassword);

        // Create vault with RSA keys
        await createVault({
          ...data,
          public_key: keyPair.publicKey,
          encrypted_private_key: encryptedPrivateKey.encrypted_private_key,
          private_key_iv: encryptedPrivateKey.private_key_iv,
          private_key_salt: encryptedPrivateKey.private_key_salt,
          private_key_auth_tag: encryptedPrivateKey.private_key_auth_tag
        });
      }
      setShowVaultModal(false);
      setEditingVault(null);
      fetchVaults();
    } catch (error) {
      console.error("Error saving vault:", error);
    } finally {
      setIsSavingVault(false);
    }
  };

  const handleSaveEntry = async (data: CreateCredentialEntryData) => {
    if (!editingEntry) return;
    try {
      if (editingEntry.entry) {
        await updateCredentialEntry(editingEntry.vault.vault_id, editingEntry.entry.entry_id, data as UpdateCredentialEntryData);
        toast.success("Credential updated successfully");
      } else {
        await createCredentialEntry(editingEntry.vault.vault_id, data);
        toast.success("Credential created successfully");
      }
      setIsManagementViewActive(false);
      setEditingEntry(null);
      setShowValues({});
      fetchVaults();
    } catch (error) {
      console.error("Error saving entry:", error);
      toast.error(editingEntry.entry ? "Failed to update credential" : "Failed to create credential");
    }
  };

  const renderCredentialManagement = () => {
    if (!selectedVault) return null;

    const isEditing = !!editingEntry?.entry;
    return (
      <div className="p-0">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-[#0a0f1d]">
            {isEditing ? `Editing: ${editingEntry.entry?.label}` : "Credential Management"}
          </h1>
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setIsManagementViewActive(false);
                setEditingEntry(null);
                setShowValues({});
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-[#050b1a] rounded-xl overflow-hidden mb-8 p-1">
          <div className="flex items-center gap-1">
            {[
              { id: 'single', label: 'Single Credential', icon: FiKey },
              { id: 'bulk', label: 'Bulk Add', icon: FiPlus },
              { id: 'import', label: 'Import', icon: FiUpload },
              { id: 'links', label: 'Program Links', icon: FiLink },
            ].filter(tab => {
              if (tab.id === 'links') return canManage || vaultPermissions[selectedVault.vault_id]?.can_link;
              return canManage || vaultPermissions[selectedVault.vault_id]?.can_add;
            }).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setManagementTab(tab.id as any)}
                className={`flex items-center gap-2.5 px-8 py-5 text-[14px] transition-all relative ${
                  managementTab === tab.id ? 'text-[#66d100]' : 'text-slate-200 hover:text-white'
                }`}
              >
                <tab.icon className={`w-4 h-4 ${managementTab === tab.id ? 'text-[#66d100]' : 'text-slate-400'}`} />
                {tab.label}
                {managementTab === tab.id && (
                  <div className="absolute bottom-2 left-8 right-8 h-[2px] bg-[#66d100] rounded-full shadow-[0_0_10px_rgba(102,209,0,0.3)]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {managementTab === 'single' && (
          <>
            {/* Step Indicator */}
            <div className="flex items-center justify-start gap-2 mb-12">
              {[
                { step: 1, label: 'Basic Info' },
                { step: 2, label: 'Credentials' },
                { step: 3, label: 'Advanced' },
              ].map((s, idx) => (
                <React.Fragment key={s.step}>
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center gap-3 px-6 py-2 rounded-full text-sm font-bold ${
                      managementStep === s.step
                        ? 'bg-[#0a0f1d] text-white shadow-lg'
                        : 'bg-[#edf2f7] text-[#718096]'
                    }`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                        managementStep === s.step ? 'bg-[#1e293b] text-white' : 'bg-[#cbd5e0] text-[#718096]'
                      }`}>
                        {s.step}
                      </span>
                      {s.label}
                    </div>
                  </div>
                  {idx < 2 && (
                    <div className="w-16 h-[2px] bg-[#edf2f7]" />
                  )}
                </React.Fragment>
              ))}
            </div>

            {managementStep === 1 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                <div>
                  <h3 className="text-sm font-bold text-[#0a0f1d] mb-4">Credential Type</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(showAllTypes ? credentialTypes : credentialTypes.slice(0, 6)).map((type: { id: string; label: string; description: string; icon: any }) => (
                      <div
                        key={type.id}
                        onClick={() => {
                          setSelectedType(type.id);
                          setCredentialEntries(getDefaultEntries(type.id as any));
                        }}
                        className={`p-4 rounded-xl border cursor-pointer transition-all relative group h-20 flex items-center ${
                          selectedType === type.id
                            ? 'border-[#66d100] bg-white shadow-sm ring-1 ring-[#66d100]/20'
                            : 'border-slate-100 hover:border-slate-200 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            selectedType === type.id ? 'bg-[#e6f4ea]' : 'bg-[#f8f9fa]'
                          }`}>
                            <type.icon className={`w-5 h-5 ${selectedType === type.id ? 'text-[#66d100]' : 'text-slate-400'}`} />
                          </div>
                          <div className="min-w-0 pr-6">
                            <h4 className="text-[13px] font-bold text-[#0a0f1d] truncate">{type.label}</h4>
                            <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1 font-medium">{type.description}</p>
                          </div>
                        </div>
                        {selectedType === type.id && (
                          <div className="absolute top-4 right-4 w-2.5 h-2.5 bg-[#66d100] rounded-full shadow-sm ring-2 ring-white" />
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowAllTypes(!showAllTypes)}
                    className="mt-6 text-[13px] font-bold text-[#66d100] hover:text-[#5ab900] transition-colors"
                  >
                    {showAllTypes ? "Show fewer types..." : "Show more types..."}
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-[13px] font-bold text-[#1a202c] mb-3">
                      Label <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <input
                      type="text"
                      value={credentialLabel}
                      onChange={(e) => setCredentialLabel(e.target.value)}
                      className="w-full px-6 py-4 bg-white border border-[#edf2f7] rounded-xl focus:ring-4 focus:ring-green-500/5 focus:border-green-500 transition-all text-sm placeholder:text-gray-300 shadow-sm"
                      placeholder="e.g., Admin Portal, User Role Credentials"
                    />
                  </div>
                    <div className="relative">
                      <label className="block text-[13px] font-bold text-[#1a202c] mb-3">
                        URL / Service
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={targetUrl}
                          onChange={(e) => setTargetUrl(e.target.value)}
                          onFocus={() => setShowTargetDropdown(true)}
                          onBlur={() => setTimeout(() => setShowTargetDropdown(false), 200)}
                          className="w-full px-6 py-4 bg-white border border-[#edf2f7] rounded-xl focus:ring-4 focus:ring-green-500/5 focus:border-green-500 transition-all text-sm placeholder:text-gray-300 shadow-sm"
                          placeholder="https://example.com or select a target"
                        />
                        <FiChevronDown className={`absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none transition-transform ${showTargetDropdown ? 'rotate-180' : ''}`} />
                      </div>
                    <AnimatePresence>
                      {showTargetDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute z-10 w-full mt-2 bg-white border border-[#edf2f7] rounded-2xl shadow-xl max-h-60 overflow-y-auto custom-scrollbar"
                        >
                          {loadingTargets ? (
                            <div className="p-6 text-center">
                              <div className="w-5 h-5 border-2 border-[#66d100] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                              <p className="text-[11px] text-gray-500 font-bold">Loading targets from assets...</p>
                            </div>
                          ) : allTargets.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <p className="text-[11px] font-bold mb-1">No active assets or targets found.</p>
                              <p className="text-[10px]">Targets are pulled from your asset inventory.</p>
                            </div>
                          ) : (() => {
                            const searchQuery = (targetUrl || "").toLowerCase();
                            const filtered = allTargets.filter((t: {targetName: string, targetType: string}) => {
                              const name = (t.targetName || "").toLowerCase();
                              const type = (t.targetType || "").toLowerCase();
                              return name.includes(searchQuery) || type.includes(searchQuery);
                            });

                            const countLabel = searchQuery 
                              ? `Matched ${filtered.length} of ${allTargets.length} targets`
                              : `Showing all ${allTargets.length} targets`;
                            
                            if (filtered.length === 0) {
                              return (
                                <div className="p-6 text-center">
                                  <p className="text-[11px] text-gray-400 font-bold mb-1">No matches for "{targetUrl}"</p>
                                  <p className="text-[10px] text-gray-300">Try a different search term</p>
                                </div>
                              );
                            }

                            return (
                              <>
                                <div className="px-6 py-2 bg-gray-50 border-b border-gray-100">
                                  <p className="text-[9px] uppercase tracking-wider font-black text-gray-400">{countLabel}</p>
                                </div>
                                {filtered.map((t: {targetName: string, targetType: string}, idx: number) => (
                                  <div
                                    key={`${t.targetName}-${idx}`}
                                    onClick={() => {
                                      setTargetUrl(t.targetName);
                                      setShowTargetDropdown(false);
                                    }}
                                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
                                  >
                                    <p className="text-[13px] font-bold text-[#0a0f1d]">{t.targetName}</p>
                                    <p className="text-[10px] text-gray-500 mt-1">{t.targetType}</p>
                                  </div>
                                ))}
                              </>
                            );
                          })()}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input 
                      type="checkbox" 
                      checked={credentialIsFavorite}
                      onChange={(e) => setCredentialIsFavorite(e.target.checked)}
                      className="w-5 h-5 rounded-lg border-gray-200 text-[#66d100] focus:ring-[#66d100]" 
                    />
                    <span className="text-sm font-bold text-[#4a5568] group-hover:text-[#0a0f1d]">Mark as favourite</span>
                  </label>
                </div>

                <div className="flex items-center justify-end gap-8 pt-8 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setIsManagementViewActive(false);
                      resetManagementStates();
                    }}
                    className="text-sm font-bold text-[#718096] hover:text-[#4a5568] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!credentialLabel) {
                        toast.error("Please enter a label");
                        return;
                      }
                      setManagementStep(2);
                    }}
                    className="px-10 py-4 bg-[#66d100] text-white rounded-xl hover:bg-[#5ab900] transition-all font-bold text-sm shadow-lg shadow-green-100"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {managementStep === 2 && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-[#0a0f1d]">Credentials</h3>
                    <p className="text-sm text-gray-500">Provide the sensitive technical details.</p>
                  </div>
                  <button
                    onClick={() => setCredentialEntries([...credentialEntries, { key: "New Field", value: "", type: "text" }])}
                    className="flex items-center gap-2 text-[#66d100] text-sm font-bold hover:bg-[#f0fff4] px-4 py-2 rounded-xl transition-all"
                  >
                    <FiPlus className="w-4 h-4" />
                    Add Field
                  </button>
                </div>

                <div className="space-y-4">
                  {credentialEntries.map((entry, idx) => (
                    <div key={idx} className="flex flex-col lg:flex-row items-start lg:items-end gap-4 p-6 bg-slate-50/50 rounded-2xl border border-slate-100 group transition-all hover:border-slate-200">
                      <div className="w-full lg:w-32 space-y-2">
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Type</label>
                        <select
                          value={entry.type}
                          onChange={(e) => {
                            const newEntries = [...credentialEntries];
                            newEntries[idx].type = e.target.value as 'text' | 'password';
                            setCredentialEntries(newEntries);
                          }}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-[13px] font-medium"
                        >
                          <option value="text">Text</option>
                          <option value="password">Password</option>
                        </select>
                      </div>

                      <div className="w-full lg:w-64 space-y-2">
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Field Name</label>
                        <input
                          type="text"
                          value={entry.key}
                          onChange={(e) => {
                            const newEntries = [...credentialEntries];
                            newEntries[idx].key = e.target.value;
                            setCredentialEntries(newEntries);
                          }}
                          placeholder="e.g., Username"
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-[13px] font-medium"
                        />
                      </div>

                      <div className="flex-1 w-full space-y-2">
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Value</label>
                        <div className="relative">
                          <input
                            type={entry.type === 'password' && !showValues[idx] ? 'password' : 'text'}
                            value={entry.value}
                            onChange={(e) => {
                              const newEntries = [...credentialEntries];
                              newEntries[idx].value = e.target.value;
                              setCredentialEntries(newEntries);
                            }}
                            placeholder="Enter value..."
                            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-[13px] font-medium pr-10"
                          />
                          {entry.type === 'password' && (
                            <button
                              type="button"
                              onClick={() => setShowValues(prev => ({ ...prev, [idx]: !prev[idx] }))}
                              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
                              title={showValues[idx] ? "Hide value" : "Show value"}
                            >
                              {showValues[idx] ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="lg:mb-1">
                        <button
                          onClick={() => {
                            const newEntries = credentialEntries.filter((_, i) => i !== idx);
                            setCredentialEntries(newEntries);
                          }}
                          className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                          title="Delete Field"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-8 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <FiAlertCircle className="w-4 h-4 text-blue-400" />
                    <p className="text-[12px] text-blue-500 font-bold italic">
                      Step 3: Advanced section is optional
                    </p>
                  </div>
                  <div className="flex items-center gap-8">
                    <button
                      onClick={() => setManagementStep(1)}
                      className="text-sm font-bold text-[#718096] hover:text-[#4a5568] transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => {
                        const emptyFields = credentialEntries.filter(e => !e.value.trim());
                        if (emptyFields.length > 0) {
                          toast.error(`Please provide values for: ${emptyFields.map(f => f.key).join(", ")}`);
                          return;
                        }
                        setManagementStep(3);
                      }}
                      className="px-10 py-4 bg-[#66d100] text-white rounded-2xl hover:bg-[#5ab900] transition-all font-bold text-sm shadow-lg shadow-green-100"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {managementStep === 3 && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                <div>
                  <h3 className="text-lg font-bold text-[#0a0f1d] mb-4">Advanced Options</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-[13px] font-bold text-[#4a5568] mb-3">
                        Secure Notes
                      </label>
                      <textarea
                        value={credentialNotes}
                        onChange={(e) => setCredentialNotes(e.target.value)}
                        className="w-full px-6 py-4 bg-[#f9fafb] border border-[#edf2f7] rounded-2xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 focus:bg-white transition-all text-sm placeholder:text-gray-300 min-h-[150px] resize-none"
                        placeholder="Add some context or specific instructions for this credential..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-bold text-slate-800">Favorite Entry</h4>
                          <button 
                            onClick={() => setCredentialIsFavorite(!credentialIsFavorite)}
                            className={`w-12 h-6 rounded-full transition-all relative ${credentialIsFavorite ? 'bg-[#66d100]' : 'bg-slate-200'}`}
                          >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${credentialIsFavorite ? 'left-7' : 'left-1'}`} />
                          </button>
                        </div>
                        <p className="text-[11px] text-slate-500">Quick access in your favorites list</p>
                      </div>

                      <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                         <div className="flex items-center gap-4 mb-4">
                           <div className="flex-1">
                             <h4 className="text-sm font-bold text-slate-800">Expiry Date</h4>
                             <p className="text-[11px] text-slate-500">Optional: reminder before it expires</p>
                           </div>
                         </div>
                         <div className="space-y-4">
                            <input
                              type="date"
                              value={credentialExpiresAt}
                              onChange={(e) => setCredentialExpiresAt(e.target.value)}
                              className="w-full px-4 py-2 bg-white border border-[#edf2f7] rounded-xl text-xs"
                            />
                            {credentialExpiresAt && (
                              <select
                                value={credentialRotationDays}
                                onChange={(e) => setCredentialRotationDays(parseInt(e.target.value))}
                                className="w-full px-4 py-2 bg-white border border-[#edf2f7] rounded-xl text-xs"
                              >
                                <option value={7}>Remind 7 days before</option>
                                <option value={14}>Remind 14 days before</option>
                                <option value={30}>Remind 30 days before</option>
                                <option value={90}>Remind 90 days before</option>
                              </select>
                            )}
                         </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-8 pt-8 border-t border-gray-100">
                  <button
                    onClick={() => setManagementStep(2)}
                    className="text-sm font-bold text-[#718096] hover:text-[#4a5568] transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={async () => {
                      if (!selectedVault) return;
                      setIsSavingEntry(true);
                      try {
                        const validEntries = credentialEntries.filter((e: any) => e.key.trim() && e.value.trim());
                        if (validEntries.length === 0 && selectedType !== 'secure_note') {
                          toast.error("Please add at least one credential field");
                          return;
                        }

                        let encryptedData;
                        if (isSubUser && selectedVault.public_key) {
                          // Sub-user mode: RSA encryption (hybrid)
                          const result = await encryptWithPublicKey(
                            { entries: validEntries },
                            selectedVault.public_key,
                            credentialNotes
                          );
                          encryptedData = {
                            encrypted_data: result.encrypted_data,
                            iv: result.iv,
                            auth_tag: result.auth_tag,
                            encrypted_credential_key: result.encrypted_aes_key,
                            encrypted_notes: result.encrypted_notes,
                            notes_iv: result.notes_iv,
                            notes_auth_tag: result.notes_auth_tag
                          };
                        } else if (masterPassword) {
                          // Owner mode: AES encryption with master password
                          const result = await encryptCredential(
                            { entries: validEntries },
                            masterPassword
                          );
                          
                          encryptedData = { ...result };

                          // Encrypt notes if present
                          if (credentialNotes?.trim()) {
                            const saltArray = new Uint8Array(base64ToArrayBuffer(result.salt));
                            const noteResult = await encryptNotes(
                              credentialNotes,
                              masterPassword,
                              saltArray,
                              result.key_iterations
                            );
                            if (noteResult) {
                              encryptedData = {
                                ...encryptedData,
                                ...noteResult
                              };
                            }
                          }
                        } else {
                          toast.error("Encryption session expired. Please re-enter master password.");
                          setShowMasterPasswordModal(true);
                          return;
                        }

                        await handleSaveEntry({
                          label: credentialLabel,
                          credential_type: selectedType as any,
                          url: targetUrl,
                          notes: credentialNotes,
                          is_favorite: credentialIsFavorite,
                          expires_at: credentialExpiresAt || undefined,
                          rotation_reminder_days: credentialExpiresAt ? credentialRotationDays : undefined,
                          ...encryptedData
                        } as any);
                        setIsManagementViewActive(false);
                        resetManagementStates();
                      } catch (err) {
                        console.error(err);
                      } finally {
                        setIsSavingEntry(false);
                      }
                    }}
                    disabled={isSavingEntry}
                    className="px-10 py-4 bg-[#0a0f1d] text-white rounded-2xl hover:bg-slate-800 transition-all font-bold text-sm shadow-lg shadow-slate-200 flex items-center gap-3 disabled:opacity-50"
                  >
                    {isSavingEntry ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <FiPlus className="w-4 h-4" />
                    )}
                    {isEditing ? "Update Credential" : "Save Credential"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {managementTab === 'bulk' && (
          <div className="animate-in fade-in slide-in-from-bottom-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-lg font-bold text-[#0a0f1d]">Bulk Add Credentials</h2>
                <p className="text-sm text-gray-500">Vault: {selectedVault.name}</p>
              </div>
            </div>

              <div className="space-y-6">
                {bulkEntryRows.map((row, idx) => (
                  <div key={row.id} className="p-8 bg-white border border-gray-100 rounded-[32px] shadow-sm relative group">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#66d100] rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {idx + 1}
                        </div>
                        <h3 className="font-bold text-[#0a0f1d]">Credential Details</h3>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={row.is_favorite}
                            onChange={(e) => {
                              const newRows = [...bulkEntryRows];
                              newRows[idx].is_favorite = e.target.checked;
                              setBulkEntryRows(newRows);
                            }}
                            className="w-4 h-4 rounded border-gray-200 text-[#66d100] focus:ring-[#66d100]" 
                          />
                          <span className="text-xs font-bold text-[#718096]">Favourite</span>
                          <FiAward className={`w-4 h-4 ${row.is_favorite ? 'text-amber-400' : 'text-gray-300'}`} />
                        </label>
                        {bulkEntryRows.length > 1 && (
                          <button 
                            onClick={() => setBulkEntryRows(bulkEntryRows.filter(r => r.id !== row.id))}
                            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                      <div className="space-y-2">
                        <label className="text-[13px] font-bold text-slate-500">Type</label>
                        <select
                          value={row.type}
                          onChange={(e) => {
                            const newRows = [...bulkEntryRows];
                            newRows[idx].type = e.target.value;
                            newRows[idx].entries = getDefaultEntries(e.target.value as any);
                            setBulkEntryRows(newRows);
                          }}
                          className="w-full px-5 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-sm"
                        >
                          {credentialTypes.map((t: { id: string; label: string }) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[13px] font-bold text-slate-500 flex items-center gap-1">
                           Label (Name) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={row.label}
                          onChange={(e) => {
                            const newRows = [...bulkEntryRows];
                            newRows[idx].label = e.target.value;
                            setBulkEntryRows(newRows);
                          }}
                          placeholder="e.g., Admin Portal"
                          className="w-full px-5 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[13px] font-bold text-slate-500">URL / Service Target</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={row.url}
                            onChange={(e) => {
                              const newRows = [...bulkEntryRows];
                              newRows[idx].url = e.target.value;
                              setBulkEntryRows(newRows);
                            }}
                            onFocus={() => setActiveBulkTargetDropdownId(row.id)}
                            onBlur={() => setTimeout(() => setActiveBulkTargetDropdownId(null), 200)}
                            placeholder="https://... or select Target"
                            className="w-full px-5 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-sm"
                          />
                          <FiChevronDown className={`absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 transition-transform ${activeBulkTargetDropdownId === row.id ? 'rotate-180' : ''}`} />
                          <AnimatePresence>
                            {activeBulkTargetDropdownId === row.id && (
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto custom-scrollbar"
                              >
                                {allTargets.length > 0 ? (
                                  allTargets.filter(t => 
                                    t.targetName.toLowerCase().includes(row.url.toLowerCase())
                                  ).map((t, tIdx) => (
                                    <div
                                      key={tIdx}
                                      onClick={() => {
                                        const newRows = [...bulkEntryRows];
                                        newRows[idx].url = t.targetName;
                                        setBulkEntryRows(newRows);
                                        setActiveBulkTargetDropdownId(null);
                                      }}
                                      className="px-5 py-3 hover:bg-gray-50 cursor-pointer text-[12px] border-b border-gray-50 last:border-0"
                                    >
                                      <p className="font-bold text-slate-800">{t.targetName}</p>
                                      <p className="text-[10px] text-slate-400">{t.targetType}</p>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-4 text-center text-[10px] text-slate-400 font-bold">No targets found</div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-8">
                       <label className="text-[13px] font-bold text-slate-500 mb-2 block">Secure Notes</label>
                       <textarea
                         value={row.notes}
                         onChange={(e) => {
                           const newRows = [...bulkEntryRows];
                           newRows[idx].notes = e.target.value;
                           setBulkEntryRows(newRows);
                         }}
                         placeholder="Instructions or context..."
                         className="w-full px-5 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-sm min-h-[80px] resize-none"
                       />
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-[13px] font-bold text-slate-400 uppercase tracking-widest">Fields</h4>
                      <button
                        onClick={() => {
                          const newRows = [...bulkEntryRows];
                          newRows[idx].entries = [...(newRows[idx].entries || []), { key: "New Field", value: "", type: "text" }];
                          setBulkEntryRows(newRows);
                        }}
                        className="flex items-center gap-2 text-[#66d100] text-[12px] font-bold hover:bg-[#66d100]/5 px-3 py-1.5 rounded-lg transition-all"
                      >
                        <FiPlus className="w-3.5 h-3.5" />
                        Add Field
                      </button>
                    </div>

                    <div className="space-y-4">
                      {row.entries?.map((field: any, fIdx: number) => (
                        <div key={fIdx} className="flex flex-col lg:flex-row items-start lg:items-end gap-4 p-5 bg-slate-50/50 rounded-2xl border border-slate-100 group transition-all hover:border-slate-200">
                          <div className="w-full lg:w-32 space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Type</label>
                            <select
                              value={field.type}
                              onChange={(e) => {
                                const newRows = [...bulkEntryRows];
                                newRows[idx].entries[fIdx].type = e.target.value as 'text' | 'password';
                                setBulkEntryRows(newRows);
                              }}
                              className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-xs font-semibold"
                            >
                              <option value="text">Text</option>
                              <option value="password">Password</option>
                            </select>
                          </div>

                          <div className="w-full lg:w-48 space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Name</label>
                            <input
                              type="text"
                              value={field.key}
                              onChange={(e) => {
                                const newRows = [...bulkEntryRows];
                                newRows[idx].entries[fIdx].key = e.target.value;
                                setBulkEntryRows(newRows);
                              }}
                              className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-xs font-semibold"
                            />
                          </div>

                          <div className="flex-1 w-full space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Value</label>
                            <div className="relative">
                              <input
                                type={field.type === 'password' ? 'password' : 'text'}
                                value={field.value}
                                onChange={(e) => {
                                  const newRows = [...bulkEntryRows];
                                  newRows[idx].entries[fIdx].value = e.target.value;
                                  setBulkEntryRows(newRows);
                                }}
                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500/10 focus:border-green-500 outline-none transition-all text-xs font-semibold pr-10"
                                placeholder="Value..."
                              />
                              {field.type === 'password' && (
                                <FiEye className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 w-3.5 h-3.5" />
                              )}
                            </div>
                          </div>

                          <div className="lg:mb-0.5">
                            <button
                              onClick={() => {
                                const newRows = [...bulkEntryRows];
                                newRows[idx].entries = newRows[idx].entries.filter((_: any, i: number) => i !== fIdx);
                                setBulkEntryRows(newRows);
                              }}
                              className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => setBulkEntryRows([...bulkEntryRows, { id: Date.now(), type: 'password', label: '', url: '', entries: getDefaultEntries('password'), is_favorite: false }])}
                  className="w-full py-6 border-2 border-dashed border-gray-200 rounded-xl hover:border-[#66d100] hover:bg-[#66d100]/5 text-gray-400 hover:text-[#66d100] transition-all flex items-center justify-center gap-3 group"
                >
                  <FiPlus className="w-5 h-5" />
                  <span className="font-bold text-sm">Add Another Credential</span>
                </button>

                <div className="flex items-center justify-between pt-12 border-t border-gray-100">
                   <div className="flex items-center gap-3 text-xs text-gray-400">
                     <FiLock className="text-[#66d100]" />
                     Data is encrypted locally before transmission.
                   </div>
                   <div className="flex items-center gap-8">
                     <button
                       onClick={() => {
                         setIsManagementViewActive(false);
                         resetManagementStates();
                       }}
                       className="text-sm font-bold text-[#718096] hover:text-[#4a5568]"
                     >
                       Cancel
                     </button>
                     <button
                       onClick={async () => {
                         if (!selectedVault) return;
                         setIsSavingEntry(true);
                         try {
                           const validRows = bulkEntryRows.filter(r => r.label.trim() && r.entries.some((e: any) => e.value.trim()));
                           if (validRows.length === 0) {
                             toast.error("Please provide at least one complete credential");
                             return;
                           }

                           // Final encryption and save logic
                           const savePromises = validRows.map(async (row) => {
                             const entries = row.entries;
                             let encryptedData;
                             if (isSubUser && selectedVault.public_key) {
                               const result = await encryptWithPublicKey({ entries }, selectedVault.public_key, row.notes);
                               encryptedData = {
                                 encrypted_data: result.encrypted_data,
                                 iv: result.iv,
                                 auth_tag: result.auth_tag,
                                 encrypted_credential_key: result.encrypted_aes_key,
                                 encrypted_notes: result.encrypted_notes,
                                 notes_iv: result.notes_iv,
                                 notes_auth_tag: result.notes_auth_tag
                               };
                             } else if (masterPassword) {
                               const result = await encryptCredential({ entries }, masterPassword);
                               encryptedData = { ...result };
                               
                               // Encrypt notes if present
                               if (row.notes?.trim()) {
                                 const saltArray = new Uint8Array(base64ToArrayBuffer(result.salt));
                                 const noteResult = await encryptNotes(row.notes, masterPassword, saltArray, result.key_iterations);
                                 if (noteResult) {
                                   encryptedData = {
                                     ...encryptedData,
                                     ...noteResult
                                   };
                                 }
                               }
                             }

                             return createCredentialEntry(selectedVault.vault_id, {
                               label: row.label,
                               credential_type: row.type as any,
                               url: row.url,
                               is_favorite: row.is_favorite,
                               ...encryptedData
                             } as any);
                           });

                           await Promise.all(savePromises);
                           toast.success(`${validRows.length} credentials saved successfully`);
                           setIsManagementViewActive(false);
                           resetManagementStates();
                           fetchVaults();
                         } catch (err) {
                           console.error(err);
                           toast.error("Failed to save some credentials");
                         } finally {
                           setIsSavingEntry(false);
                         }
                       }}
                       disabled={isSavingEntry}
                       className="px-10 py-4 bg-[#66d100] text-white rounded-2xl hover:bg-[#5ab900] transition-all font-bold text-sm shadow-lg shadow-green-100 flex items-center gap-3 disabled:opacity-50"
                     >
                       {isSavingEntry ? (
                         <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                       ) : (
                         "Save All Credentials"
                       )}
                     </button>
                   </div>
                </div>
              </div>
           </div>
        )}

        {managementTab === 'import' && (
           <div className="animate-in fade-in slide-in-from-bottom-4 space-y-8">
              {/* 1. Download Template */}
              <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
                <h3 className="text-lg font-bold text-[#0a0f1d] mb-2">1. Download Template</h3>
                <p className="text-sm text-gray-500 mb-8">Download a sample template, fill in your credentials, then upload the file.</p>
                
                <div className="flex items-center gap-4 mb-8">
                  <button 
                    onClick={() => handleDownloadTemplate('csv')}
                    className="flex items-center gap-2 px-6 py-3 bg-[#66d100] text-white rounded-xl font-bold text-sm hover:bg-[#5ab900] transition-all"
                  >
                    <FiDownload className="w-4 h-4" />
                    CSV Template
                  </button>
                  <button 
                    onClick={() => handleDownloadTemplate('json')}
                    className="flex items-center gap-2 px-6 py-3 bg-[#0a0f1d] text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all"
                  >
                    <FiDownload className="w-4 h-4" />
                    JSON Template
                  </button>
                </div>

                <div className="p-8 bg-[#f0f9eb] rounded-2xl border border-[#e2f2d9]">
                  <h4 className="text-[13px] font-bold text-[#2d5a27] mb-6">Field Reference</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-4">
                    <div className="space-y-4">
                      <p className="text-[12px]"><span className="text-red-500 font-bold">*</span> <span className="font-bold text-[#1a202c]">label</span> <span className="text-gray-400">— Name for the credential</span></p>
                      <p className="text-[12px]"><span className="font-bold text-[#1a202c]">credential_type</span> <span className="text-gray-400">— password, api_key, ssh_key, database, server, certificate, wifi, secure_note, credit_card, custom</span></p>
                      <p className="text-[12px]"><span className="font-bold text-[#1a202c]">username</span> <span className="text-gray-400">— Username/Identifier</span></p>
                      <p className="text-[12px]"><span className="font-bold text-[#1a202c]">is_favorite</span> <span className="text-gray-400">— true/false</span></p>
                      <p className="text-[12px]"><span className="font-bold text-[#1a202c]">rotation_reminder_days</span> <span className="text-gray-400 ml-2">— Days before expiry alert</span></p>
                    </div>
                    <div className="space-y-4">
                      <p className="text-[12px]"><span className="text-red-500 font-bold">*</span> <span className="font-bold text-[#1a202c]">password</span> <span className="text-gray-400">— Secret value to encrypt</span></p>
                      <p className="text-[12px]"><span className="font-bold text-[#1a202c]">url</span> <span className="text-gray-400">— Service URL</span></p>
                      <p className="text-[12px]"><span className="font-bold text-[#1a202c]">notes</span> <span className="text-gray-400">— Additional notes</span></p>
                      <p className="text-[12px]"><span className="font-bold text-[#1a202c]">expires_at</span> <span className="text-gray-400">— YYYY-MM-DD format</span></p>
                    </div>
                  </div>
                  <p className="mt-8 text-[11px] text-red-500 font-bold uppercase tracking-wider">* = Required field</p>
                </div>
              </div>

              {/* 2. Upload Your File */}
              <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
                <h3 className="text-lg font-bold text-[#0a0f1d] mb-6">2. Upload Your File</h3>
                
                <div 
                  className={`border-2 border-dashed rounded-[32px] p-24 flex flex-col items-center justify-center transition-all ${isDragging ? 'border-[#66d100] bg-[#66d100]/10 shadow-inner' : 'border-gray-200 bg-gray-50/50'}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); if(e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0]); }}
                >
                   <div className="w-20 h-20 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6">
                    <FiFileText className={`w-8 h-8 transition-colors ${isDragging ? 'text-[#66d100]' : 'text-gray-300'}`} />
                   </div>
                  <p className="text-sm text-gray-400 font-bold mb-8">Drag and drop your CSV or JSON file here, or</p>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="px-10 py-4 bg-[#66d100] text-white rounded-2xl font-bold text-sm hover:bg-[#5ab900] transition-all shadow-lg shadow-green-100"
                  >
                    Browse Files
                  </button>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    accept=".csv,.json"
                    onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-8 border-t border-gray-100">
                <p className="text-xs text-gray-400 font-medium">Supported formats: .csv, .json (max 5MB)</p>
                <button 
                  onClick={() => {
                    setIsManagementViewActive(false);
                    resetManagementStates();
                  }}
                  className="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
         )}

         {managementTab === 'links' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
             <div className="flex flex-col gap-1 mb-8">
                <h2 className="text-xl font-bold text-[#0a0f1d]">Manage Program Links</h2>
                <p className="text-sm text-gray-400 font-medium tracking-tight">Connect this vault to specific programs and shared resources</p>
             </div>

             <button
                onClick={() => handleLinkToProgram(selectedVault!)}
                className="w-full py-8 border-2 border-dashed border-gray-100 rounded-[28px] hover:border-[#66d100]/50 hover:bg-[#66d100]/5 text-gray-300 hover:text-[#66d100] transition-all flex items-center justify-center gap-4 group mb-8"
             >
                <div className="w-10 h-10 rounded-full border-2 border-dashed border-gray-100 flex items-center justify-center group-hover:border-[#66d100]/30 transition-colors">
                  <FiPlus className="w-5 h-5" />
                </div>
                <span className="font-bold text-base">Add Program Link</span>
             </button>

             <div className="space-y-4">
                {loadingLinkedPrograms ? (
                   <div className="py-20 flex flex-col items-center justify-center gap-4 bg-gray-50/50 rounded-[32px] border border-gray-100">
                      <div className="w-8 h-8 border-4 border-[#66d100]/20 border-t-[#66d100] rounded-full animate-spin" />
                      <p className="text-sm text-gray-400 font-bold font-mono">Scanning links...</p>
                   </div>
                ) : linkedPrograms.length > 0 ? (
                   <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                     {linkedPrograms.map((linked, idx) => (
                        <div key={idx} className="bg-white rounded-[24px] border border-gray-100 p-8 flex items-center justify-between group hover:border-[#66d100]/30 hover:shadow-xl hover:shadow-green-50/50 transition-all cursor-default relative">
                           <div className="flex items-center gap-6">
                              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm ${
                                 idx % 3 === 0 ? 'bg-blue-50 text-blue-500' :
                                 idx % 3 === 1 ? 'bg-purple-50 text-purple-500' :
                                 'bg-amber-50 text-amber-500'
                              }`}>
                                 <FiLink className="w-6 h-6" />
                              </div>
                              <div>
                                 <h3 className="text-lg font-bold text-[#0a0f1d] mb-1">{linked.programTitle}</h3>
                                 <p className="text-sm text-gray-400 font-medium">
                                 Connection Ready
                              </p>
                           </div>
                        </div>
                        <div className="flex items-center gap-3">
                           <button 
                              onClick={() => {
                                setProgramToUnlink({ programId: linked.programId, title: linked.programTitle });
                              }}
                              className="p-3 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                           >
                              <FiTrash2 className="w-5 h-5" />
                           </button>
                        </div>
                        </div>
                     ))}
                   </div>
                ) : (
                   <div className="py-20 flex flex-col items-center justify-center gap-6 bg-gray-50/20 rounded-[32px] border border-dashed border-gray-100">
                      <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center">
                         <FiLink className="w-8 h-8 text-gray-100" />
                      </div>
                      <div className="text-center">
                         <p className="text-base font-bold text-gray-300 mb-1">No active program links</p>
                         <p className="text-[13px] text-gray-300 max-w-xs mx-auto">This vault is currently isolated</p>
                      </div>
                   </div>
                )}
             </div>
             <div className="mt-8 flex items-center gap-3 text-xs text-gray-400 font-medium pb-8">
                <FiShield className="text-[#66d100] w-4 h-4" />
                Researchers will use an access password to view credentials.
             </div>
          </div>
        )}
      </div>
    );
  };


  // Show lock screen if session is locked after timeout
  if (isSessionLocked && wasTimedOut) {
    return (
      <div className="min-h-screen bg-slate-100 p-6">
        <div className="max-w-2xl mx-auto bg-white rounded-lg border border-gray-200 shadow-sm">
          <VaultLockScreen
            onUnlock={handleUnlockFromLockScreen}
            isRelock={true}
            title="Session Expired"
            description="Your vault session has been locked due to inactivity. Enter your master password to continue."
          />
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="flex bg-[#f1f5f9] h-[calc(100vh-65px)] p-4 gap-1 overflow-hidden"> 
      <div className="w-[300px] bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col flex-shrink-0 h-full overflow-hidden">
        <div className="p-4 pt-8 pb-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#1a202c] rounded-2xl flex items-center justify-center shadow-lg shadow-gray-200">
              <FiShield className="w-6 h-6 text-[#66d100]" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0a0f1d] leading-none mb-1.5">Credential Vault</h1>
              <span className="inline-block px-2 py-1 text-[9px] font-bold bg-[#ecf9e6] text-[#66d100] rounded-md uppercase tracking-wider leading-none">
                Beta
              </span>
            </div>
          </div>

          <div className="relative mb-6">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-5 py-3 bg-[#f8fafc] border border-gray-100 rounded-2xl text-[13px] text-[#0a0f1d] placeholder:text-gray-300 focus:ring-2 focus:ring-[#66d100]/10 focus:border-[#66d100] transition-all"
            />
          </div>

          {canManage && (
            <button
              onClick={handleAddVault}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#66d100] text-white rounded-2xl hover:bg-[#5ab900] transition-all font-bold text-xs shadow-lg shadow-green-100"
            >
              <FiPlus className="w-5 h-5" />
              Create Vault
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-1 pb-6 custom-scrollbar">
          <h2 className="text-[11px] font-bold text-[#a0acc0] uppercase tracking-[0.1em] px-4 mb-5">
            Your Vaults
          </h2>
          <div className="space-y-1">
            {vaults.filter(v => 
              v.name.toLowerCase().includes(searchQuery.toLowerCase())
            ).map((vault) => (
              <div
                key={vault.vault_id}
                onClick={() => handleVaultSelect(vault.vault_id)}
                className={`flex items-center justify-between p-4 px-5 rounded-2xl cursor-pointer transition-all group relative ${
                  selectedVaultId === vault.vault_id
                    ? "bg-[#ecf7e7]"
                    : "hover:bg-gray-50/80"
                }`}
              >
                {selectedVaultId === vault.vault_id && (
                  <div className="absolute left-0 top-0 bottom-0 w-[4.5px] bg-[#66d100] rounded-l-2xl shadow-[0_0_10px_rgba(102,209,0,0.2)]" />
                )}
                <div className="flex items-center gap-4 min-w-0">
                  <FiFolder className={`w-5 h-5 flex-shrink-0 transition-colors ${
                    selectedVaultId === vault.vault_id ? "text-[#66d100]" : "text-[#a0acc0]"
                  }`} />
                  <span className={`text-[12.5px] truncate transition-all ${
                    selectedVaultId === vault.vault_id ? "font-bold text-[#0a0f1d]" : "font-bold text-[#a0acc0]"
                  }`}>
                    {vault.name}
                  </span>
                </div>
                <span className={`flex items-center justify-center min-w-[22px] h-5 px-1.5 text-[10px] rounded-full font-bold transition-all ${
                  selectedVaultId === vault.vault_id 
                    ? "bg-[#c6f6d5] text-[#22543d]" 
                    : "bg-[#1a202c] text-white"
                }`}>
                  {vault.entries?.length || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-8 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#a0acc0]">
            <div className="relative">
              <FiLock className="w-5 h-5" />
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-tight">AES-256 Encrypted</span>
          </div>
          {canManage && (
            <Link
              to="/dashboard/vault/guide"
              className="p-2 text-gray-300 hover:text-[#66d100] transition-colors"
            >
              <FiBookOpen className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>

        {/* Main Content Area: 3-Row Layout */}
      <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0 pl-2 pr-4 pb-4 pt-0 space-y-4">
        {selectedVault ? (
          <>
            {/* ROW 1: Vault Header */}
            <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h1 className="text-xl font-bold text-slate-800">{selectedVault.name}</h1>
                  <p className="text-[11px] text-gray-400 font-medium">Vault ID: {selectedVault.vault_id}</p>
                </div>
                 <div className="flex items-center gap-3">
                   {(canManage || vaultPermissions[selectedVault.vault_id]?.can_link) && (
                     <button
                       onClick={() => {
                         setManagementTab('links');
                         setIsManagementViewActive(true);
                       }}
                       className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 text-slate-700 rounded-xl hover:bg-gray-50 transition-all font-bold text-xs shadow-sm"
                     >
                       <FiLink className="w-4 h-4 text-slate-400" />
                       Link to a Program
                     </button>
                   )}
                   {(canManage || vaultPermissions[selectedVault.vault_id]?.can_add) && (
                     <button
                       onClick={() => handleAddEntry(selectedVault!)}
                       className="flex items-center gap-2 px-6 py-3 bg-[#0f172a] text-white rounded-xl hover:bg-slate-800 transition-all font-bold text-xs shadow-sm"
                     >
                       <FiPlus className="w-4 h-4" />
                       Add Credential
                     </button>
                   )}
                   {canManage && (
                     <button
                       onClick={() => setShowMasterPasswordResetModal(true)}
                       className="flex items-center gap-2 px-4 py-3 bg-white text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-slate-900 transition-all font-bold text-xs shadow-sm"
                     >
                       <FiShield className="w-4 h-4 text-slate-500" />
                       Master Password Security
                     </button>
                   )}
                   {canManage && (
                     <div className="relative group">
                       <button className="p-2.5 text-gray-400 hover:text-slate-800 hover:bg-gray-100 rounded-xl transition-all outline-none">
                         <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                           <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                         </svg>
                       </button>
                       {/* Dropdown Menu */}
                       <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 opacity-0 pointer-events-none group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-all z-20">
                         <button
                           onClick={() => {
                             setPermissionManagerVault(selectedVault!);
                             setShowPermissionManager(true);
                           }}
                           className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 hover:text-slate-900 transition-colors flex items-center gap-2"
                         >
                           <FiUsers className="w-3.5 h-3.5" />
                           Permissions
                         </button>
                         
                         <div className="h-px bg-gray-100 my-1" />
                         <button
                           onClick={() => setShowMasterPasswordResetModal(true)}
                           className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                         >
                           <FiShield className="w-3.5 h-3.5 text-slate-500" />
                           Master Password Reset
                         </button>
                         <button
                           onClick={() => handleEditVault(selectedVault!)}
                           className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 hover:text-slate-900 transition-colors flex items-center gap-2"
                         >
                           <FiEdit2 className="w-3.5 h-3.5" />
                           Edit Vault
                         </button>
                         <button
                           onClick={() => handleDeleteVaultClick(selectedVault!)}
                           className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                         >
                           <FiTrash2 className="w-3.5 h-3.5" />
                           Delete Vault
                         </button>
                       </div>
                     </div>
                   )}
                 </div>
              </div>
            </div>

            {/* ROW 2: Banners Area */}
            <div className="space-y-3">
              {/* Security Banner */}
              <div className="flex items-center justify-between px-6 py-3.5 bg-[#f0f9eb] border border-[#e1f0d7] rounded-xl relative overflow-hidden group">
                <div className="flex items-center gap-3">
                  <FiLock className="w-4 h-4 text-[#2E7D32]" />
                  <p className="text-[11px] font-bold text-[#2E7D32] tracking-tight">
                    Zero-Knowledge Encryption - AES-256 - Unique Salt + IV
                  </p>
                </div>
              </div>

              {/* Pentest Instruction Banner - Collapsible (Compact) */}
              <div className="bg-[#f0f9eb] border border-[#e1f0d7] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <button 
                  onClick={() => setIsConventionExpanded(!isConventionExpanded)}
                  className="w-full px-4 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-[#eaf5e1] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-[#e1f0d7] text-[#2E7D32] rounded-lg">
                      <FiUsers className="w-3.5 h-3.5" />
                    </div>
                    <p className="text-[12px] font-bold text-[#204a1e]">Multi-Role Testing Convention</p>
                  </div>
                  <motion.div
                    animate={{ rotate: isConventionExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="text-[#2E7D32]/50"
                  >
                    <FiChevronDown className="w-4 h-4" />
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {isConventionExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <div className="px-4 pb-3.5 pt-0.5 ml-[42px]">
                        <p className="text-[11px] text-[#2E7D32]/80 leading-relaxed">
                          If your asset requires testing across multiple user roles, please create credentials using the <code className="bg-[#e1f0d7] px-1 py-0.5 rounded font-bold text-[#2E7D32]">pentest+[role]@capturethebug.xyz</code> format (e.g., <span className="italic">pentest+admin@capturethebug.xyz</span>). Providing these via the vault ensures our researchers can perform comprehensive security vetting across all permission levels.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* ROW 3: Management Area (Grid or Management View) */}
            <div className="min-h-[400px]">
              {isManagementViewActive ? (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden p-6">
                  {renderCredentialManagement()}
                </div>
              ) : (
                <div className="pt-4">
                  {selectedVault.entries && selectedVault.entries.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {selectedVault.entries.map((entry: any) => (
                        <div key={entry.entry_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-all group">
                          {/* Top Section: Content - Optimal Spacing & Typography */}
                          <div className="pt-7 px-8 pb-10 flex items-start gap-5">
                            <div className="w-12 h-12 rounded-full bg-[#0a0f1d] flex items-center justify-center shadow-lg shadow-gray-100 flex-shrink-0 transition-transform group-hover:scale-105">
                              <CredentialTypeIcon type={entry.credential_type} className="w-5.5 h-5.5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1 pt-1">
                              <div className="flex items-start justify-between gap-4 mb-1">
                                <h3 className="text-[15px] font-bold text-slate-800 truncate">
                                  {entry.label}
                                </h3>
                                {entry.expires_at && (
                                  <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50/50 border border-amber-100/50 text-amber-600 rounded-md text-[8px] font-black uppercase tracking-tighter">
                                    <FiClock className="w-2 h-2" />
                                    {(() => {
                                      const d = new Date(entry.expires_at);
                                      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                                    })()}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 text-slate-400 font-medium">
                                <FiLink className="w-3.5 h-3.5" />
                                <span className="text-[12px] truncate">{entry.url || 'No service URL'}</span>
                              </div>

                              {/* Reveal Details Area - Nested for Clean Layout */}
                              {decryptedEntries.has(entry.entry_id) && (
                                <div className="mt-5 pt-5 border-t border-slate-50 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                                  <div className="grid grid-cols-1 gap-3">
                                    {decryptedEntries.get(entry.entry_id)?.entries?.filter(f => f.value && f.value.trim() !== "").map((field, fIdx) => (
                                      <div key={fIdx} className="flex flex-col gap-0.5">
                                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none">{field.key}</span>
                                        <div className="flex items-center justify-between group/field">
                                          <span className="text-[12px] font-mono font-bold text-slate-700 break-all">{field.value}</span>
                                          <button 
                                            onClick={() => {
                                              navigator.clipboard.writeText(field.value);
                                              toast.success(`${field.key} copied`);
                                            }}
                                            className="p-1 opacity-0 group-hover/field:opacity-100 text-slate-300 hover:text-[#66d100] transition-all"
                                          >
                                            <FiCopy className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Secure Notes Rendering - Premium Light Contrast */}
                                  {decryptedEntries.get(entry.entry_id)?.notes && (
                                    <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl relative overflow-hidden group/notes border-l-4 border-indigo-500 shadow-sm">
                                      <div className="flex items-center gap-2 text-[9px] font-black text-indigo-600 uppercase tracking-widest mb-3 leading-none">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                        <FiFileText className="w-3 h-3" /> Encrypted Notes
                                      </div>
                                      <p className="text-[12px] text-slate-700 font-medium leading-relaxed whitespace-pre-wrap relative z-10 selection:bg-indigo-100">
                                        {decryptedEntries.get(entry.entry_id)?.notes}
                                      </p>
                                      <div className="absolute top-0 right-0 p-4 opacity-[0.03] group-hover/notes:opacity-[0.07] transition-opacity">
                                        <FiShield className="w-12 h-12 text-slate-900" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Bottom Section: Actions - Generous Padding */}
                          <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between mt-auto">
                            <div className="flex items-center gap-2">
                              {canManage ? (
                                <>
                                  <button 
                                    onClick={() => decryptedEntries.has(entry.entry_id) ? handleHideEntry(entry.entry_id) : handleViewEntry(entry)}
                                    className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[11px] font-bold text-slate-700 hover:bg-gray-50 transition-all shadow-sm"
                                  >
                                    {decryptedEntries.has(entry.entry_id) ? (
                                      <>
                                        <FiEyeOff className="w-3.5 h-3.5 text-blue-500" />
                                        Hide
                                      </>
                                    ) : (
                                      <>
                                        <FiEye className="w-3.5 h-3.5 text-slate-400" />
                                        Reveal
                                      </>
                                    )}
                                  </button>
                                  <button 
                                    onClick={() => handleCopyPassword(entry)}
                                    className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[11px] font-bold text-slate-700 hover:bg-gray-50 transition-all shadow-sm"
                                  >
                                    <FiCopy className="w-3.5 h-3.5 text-slate-400" />
                                    Copy
                                  </button>
                                </>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-500 rounded-xl text-[11px] font-medium border border-slate-200/60">
                                  <FiLock className="w-3 h-3 text-slate-400" />
                                  Encrypted with Vault Key
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              {canManage && (
                                <>
                                  <button 
                                    onClick={() => handleEditEntry(selectedVault, entry)}
                                    className="p-1.5 text-slate-300 hover:text-slate-600 transition-colors rounded-lg"
                                  >
                                    <FiEdit2 className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteEntryClick(selectedVault.vault_id, entry.entry_id, entry.label)}
                                    className="p-1.5 text-slate-300 hover:text-red-500 transition-colors rounded-lg"
                                  >
                                    <FiTrash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-20 flex flex-col items-center justify-center text-center">
                      <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                        <FiFolder className="w-10 h-10 text-gray-200" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">No credentials yet</h3>
                      <p className="text-[13px] text-gray-500 max-w-xs mb-8">
                        Organize your sensitive data by adding your first credential to this vault.
                      </p>
                      <button
                        onClick={() => handleAddEntry(selectedVault)}
                        className="flex items-center gap-2 px-8 py-3 bg-[#0f172a] text-white rounded-xl hover:bg-slate-800 transition-all font-bold text-sm shadow-lg shadow-slate-200"
                      >
                        <FiPlus className="w-5 h-5" />
                        Add First Credential
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="h-full bg-white rounded-xl border border-gray-100 flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
              <FiShield className="w-12 h-12 text-gray-200" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Welcome to your Vault</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              Select or create a vault from the sidebar to manage your encrypted credentials securely.
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <MasterPasswordModal
        isOpen={showMasterPasswordModal}
        onClose={() => {
          setShowMasterPasswordModal(false);
          setPendingAction(null);
          setForceCreateMasterPassword(false);
        }}
        onSubmit={handleMasterPasswordSubmit}
        mode={forceCreateMasterPassword ? "create" : (hasMasterPasswordBeenSet ? "unlock" : "create")}
        onSwitchToCreate={
          vaults.length === 0 && hasMasterPasswordBeenSet && !forceCreateMasterPassword
            ? () => setForceCreateMasterPassword(true)
            : undefined
        }
        onResetRequest={
          vaults.length > 0
            ? () => {
                setShowMasterPasswordModal(false);
                setShowMasterPasswordResetModal(true);
              }
            : undefined
        }
      />

      <MasterPasswordResetModal
        isOpen={showMasterPasswordResetModal}
        onClose={() => setShowMasterPasswordResetModal(false)}
        onSuccess={async (newPassword) => {
          setMasterPassword(newPassword);
          setDecryptedEntries(new Map());
          // Refetch fresh vaults and entries from the database so local state has the newly re-encrypted data
          await fetchVaults();
        }}
        currentPasswordInSession={masterPassword}
        vault={selectedVault || vaults[0]}
        allVaults={vaults}
      />

      {/* Vault Modal */}
      {showVaultModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="px-8 pt-8 pb-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#0a0f1d] rounded-2xl flex items-center justify-center shadow-lg shadow-gray-200">
                  <FiShield className="w-7 h-7 text-[#47d244]" />
                </div>
                <h2 className="text-2xl font-bold text-[#0a0f1d]">
                  {editingVault ? "Edit Vault" : "Create Vault"}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowVaultModal(false);
                  setEditingVault(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            {/* Form Content */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleSaveVault({
                  name: formData.get("name") as string,
                  description: formData.get("description") as string
                });
              }}
            >
              <div className="px-8 pb-8 space-y-6">
                <div>
                  <label className="block text-[13px] font-bold text-[#4a5568] mb-3">
                    Vault Name <span className="text-[#47d244] ml-0.5">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingVault?.name || ""}
                    required
                    className="w-full px-6 py-4 bg-[#f9fafb] border border-[#edf2f7] rounded-2xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 focus:bg-white transition-all text-sm placeholder:text-gray-300"
                    placeholder="e.g., Production APIs"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-[#4a5568] mb-3">
                    Description
                  </label>
                  <textarea
                    name="description"
                    defaultValue={editingVault?.description || ""}
                    className="w-full px-6 py-4 bg-[#f9fafb] border border-[#edf2f7] rounded-2xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 focus:bg-white transition-all text-sm placeholder:text-gray-300 min-h-[120px] resize-none"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 py-6 bg-[#f9fafb]/50 border-t border-[#edf2f7] flex items-center justify-end gap-8">
                <button
                  type="button"
                  onClick={() => {
                    setShowVaultModal(false);
                    setEditingVault(null);
                  }}
                  className="text-sm font-bold text-[#718096] hover:text-[#4a5568] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingVault}
                  className={`px-8 py-4 bg-[#66d100] text-white rounded-2xl transition-all font-bold text-sm shadow-lg shadow-green-200 ${
                    isSavingVault ? "opacity-50 cursor-not-allowed" : "hover:bg-[#5ab900]"
                  }`}
                >
                  {isSavingVault ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {editingVault ? "Saving..." : "Creating..."}
                    </span>
                  ) : (
                    editingVault ? "Save Changes" : "Create Vault"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credential Modal - supports both owner mode (master password) and sub-user mode (public key) */}
      {showCredentialModal && editingEntry && (masterPassword || (isSubUser && editingEntry.vault.public_key)) && (
        <CredentialModal
          isOpen={showCredentialModal}
          onClose={() => {
            setShowCredentialModal(false);
            setEditingEntry(null);
          }}
          onSave={handleSaveEntry}
          initialData={
            editingEntry.entry && decryptedEntries.get(editingEntry.entry.entry_id)
              ? {
                  label: editingEntry.entry.label,
                  credential_type: editingEntry.entry.credential_type as CredentialType | undefined,
                  url: editingEntry.entry.url,
                  username: editingEntry.entry.username,
                  expires_at: editingEntry.entry.expires_at,
                  rotation_reminder_days: editingEntry.entry.rotation_reminder_days,
                  is_favorite: editingEntry.entry.is_favorite,
                  entries: decryptedEntries.get(editingEntry.entry.entry_id)?.entries || [],
                  notes: decryptedEntries.get(editingEntry.entry.entry_id)?.notes || ""
                }
              : undefined
          }
          // Owner mode: pass master password for symmetric encryption
          masterPassword={!isSubUser ? masterPassword ?? undefined : undefined}
          // Sub-user mode: pass public key for asymmetric encryption (write-only)
          publicKey={isSubUser ? editingEntry.vault.public_key : undefined}
          isEditing={!!editingEntry.entry}
        />
      )}


      {/* Permission Manager Modal */}
      {showPermissionManager && permissionManagerVault && (
        <VaultPermissionManager
          vaultId={permissionManagerVault.vault_id}
          vaultName={permissionManagerVault.name}
          onClose={() => {
            setShowPermissionManager(false);
            setPermissionManagerVault(null);
          }}
        />
      )}

      {/* Link to Program Modal */}
      {showLinkProgramModal && selectedLinkVault && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            setShowLinkProgramModal(false);
            setSelectedLinkVault(null);
          }} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Clean Light Header */}
            <div className="px-6 py-5 bg-white border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-50 rounded-lg">
                  <FiLink className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Manage Program Links</h2>
                  <p className="text-[13px] text-slate-500 font-medium tracking-tight truncate max-w-[200px]">{selectedLinkVault.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowLinkProgramModal(false);
                  setSelectedLinkVault(null);
                }}
                className="p-2 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-slate-600 transition-all"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Linked Programs Section */}
              {loadingLinkedPrograms ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                </div>
              ) : linkedPrograms.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Linked Programs ({linkedPrograms.length})
                  </h3>
                  <div className="space-y-2">
                    {linkedPrograms.map((linked) => (
                      <div
                        key={linked.programId}
                        className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        {linked.profilePicture ? (
                          <img
                            src={linked.profilePicture}
                            alt={linked.programTitle}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <FiShield className="w-5 h-5 text-green-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {linked.programTitle}
                          </p>
                          <p className="text-xs text-green-600">
                            {linked.isActivated ? "Active" : "Inactive"} • {linked.accessType}
                          </p>
                        </div>
                        <button
                          onClick={() => handleUnlinkProgram(linked.programId)}
                          disabled={unlinkingProgramId === linked.programId}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          {unlinkingProgramId === linked.programId ? "Unlinking..." : "Unlink"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Programs Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Available Programs
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Select a program to link. You'll set an access password for pentesters.
                </p>

                {loadingPrograms ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                  </div>
                ) : programs.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <FiAlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No programs found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {programs
                      .filter(p => !linkedPrograms.some(lp => lp.programId === p.id))
                      .map((program) => (
                        <button
                          key={program.id}
                          onClick={() => handleSelectProgram(program)}
                          className="w-full p-3 text-left border border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            {program.profilePicture ? (
                              <img
                                src={program.profilePicture}
                                alt={program.title}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                <FiShield className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate group-hover:text-green-700">
                                {program.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                {program.isActivated ? "Active" : "Inactive"}
                              </p>
                            </div>
                            <span className="px-2.5 py-1.5 text-xs font-medium text-white bg-green-500 rounded-lg group-hover:bg-green-600">
                              Link
                            </span>
                          </div>
                        </button>
                      ))}
                    {programs.filter(p => !linkedPrograms.some(lp => lp.programId === p.id)).length === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        All programs are already linked
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Footer */}
            <div className="px-6 py-4 bg-teal-50 flex justify-end rounded-b-2xl">
              <button
                onClick={() => {
                  setShowLinkProgramModal(false);
                  setSelectedLinkVault(null);
                  setLinkedPrograms([]);
                }}
                className="px-5 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Password Modal for Linking */}
      {showAccessPasswordModal && selectedLinkVault && (
        <AccessPasswordModal
          isOpen={showAccessPasswordModal}
          onClose={() => {
            setShowAccessPasswordModal(false);
            setSelectedLinkVault(null);
            setSelectedProgram(null);
          }}
          onSubmit={handleAccessPasswordSubmit}
          vault={selectedLinkVault}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && bulkImportVault && (
        <BulkImportModal
          vaultId={bulkImportVault.vault_id}
          vaultName={bulkImportVault.name}
          onClose={() => {
            setShowBulkImportModal(false);
            setBulkImportVault(null);
          }}
          onImportComplete={() => {
            // Refresh vaults to show new credentials
            fetchVaults();
          }}
        />
      )}

      {/* Manual Bulk Entry Modal */}
      {showMultiCredentialModal && bulkImportVault && (masterPassword || (isSubUser && bulkImportVault.public_key)) && (
        <MultiCredentialModal
          isOpen={showMultiCredentialModal}
          vaultId={bulkImportVault.vault_id}
          vaultName={bulkImportVault.name}
          onClose={() => {
            setShowMultiCredentialModal(false);
            setBulkImportVault(null);
          }}
          onImportComplete={fetchVaults}
          masterPassword={masterPassword || undefined}
          publicKey={isSubUser ? bulkImportVault.public_key : undefined}
        />
      )}
      {/* Delete Vault Modal with Master Password Verification */}
      {isDeleteVaultModalOpen && vaultToDelete && (
        <DeleteVaultModal
          isOpen={isDeleteVaultModalOpen}
          onClose={() => {
            setIsDeleteVaultModalOpen(false);
            setVaultToDelete(null);
            setDeleteModalLinkedPrograms([]);
          }}
          vault={vaultToDelete}
          allVaults={vaults}
          sessionMasterPassword={masterPassword}
          linkedPrograms={deleteModalLinkedPrograms}
          isLoadingLinkedPrograms={isLoadingLinkedProgramsForDelete}
          onConfirmDelete={handleConfirmDeleteVault}
          hasMasterPasswordBeenSet={hasMasterPasswordBeenSet}
        />
      )}

      {/* Delete Credential Confirmation Modal */}
      {entryToDelete && (
        <ConfirmationModal
          isOpen={!!entryToDelete}
          onClose={() => setEntryToDelete(null)}
          onConfirm={handleConfirmDeleteEntry}
          title="Delete Credential"
          description={`Are you sure you want to delete "${entryToDelete.label}"?`}
          warning="This action is permanent and cannot be undone."
          confirmText="Delete Credential"
          cancelText="Cancel"
        />
      )}

      {/* Unlink Program Confirmation Modal */}
      {programToUnlink && (
        <ConfirmationModal
          isOpen={!!programToUnlink}
          onClose={() => setProgramToUnlink(null)}
          onConfirm={() => {
            handleUnlinkProgram(programToUnlink.programId);
            setProgramToUnlink(null);
          }}
          title="Unlink Program"
          description={`Are you sure you want to unlink this vault from "${programToUnlink.title}"?`}
          warning="Researchers will immediately lose access to this vault's credentials on this program."
          confirmText="Unlink Program"
          cancelText="Cancel"
        />
      )}
      </div>
    </>
  );
};

export default CredentialVaultPage;
